import React, { useCallback, useEffect, useState } from 'react';
import { AppState, Platform, View, Text, StyleSheet, ActivityIndicator, Alert, Share } from 'react-native';
import { useRouter } from 'expo-router';
import NetInfo from '@react-native-community/netinfo';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { exportUserData } from '../../features/auth/exportData';
import { useDb } from '../../db/client';
import { loadOrCreateProfile } from '../../features/onboarding/profileBootstrap';
import { updateRecord } from '../../db/writes';
import { profiles } from '../../db/schema';
import { SyncServiceImpl, type SyncStatus } from '../../sync';
import { isOnline } from '../../sync/netStatus';
import { parseLocaleNumber } from '../shared/parseLocaleNumber';
import { colors, fonts, spacing } from '../theme';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { Chip } from '../shared/Chip';

// Uma única instância do serviço de sync pra tela inteira (sem estado
// próprio além do client Supabase que ele cria/recebe — seguro reutilizar).
const syncService = new SyncServiceImpl();

// Único idioma disponível hoje (regra invíolável #1: pt-BR é a fonte). O
// seletor já existe para quando houver mais opções — trocar aqui não exige
// mexer na tela.
const AVAILABLE_LOCALES = ['pt-BR'] as const;

export function ProfileScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [status, setStatus] = useState<SyncStatus[] | null>(null);
  const [online, setOnline] = useState(true);
  const pendingTotal = status?.reduce((sum, item) => sum + item.pendingCount, 0) ?? 0;
  // Falha #6: contado à parte de pendingTotal — quarentena não é "vai
  // sincronizar sozinho", é uma escrita presa que precisa de atenção.
  const quarantinedTotal = status?.reduce((sum, item) => sum + item.quarantinedCount, 0) ?? 0;

  const [profileLoading, setProfileLoading] = useState(true);
  const [hourlyRateText, setHourlyRateText] = useState('0');
  const [markupText, setMarkupText] = useState('0');
  const [locale, setLocale] = useState<string>('pt-BR');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!db) return;
    const result = await syncService.getStatus(db);
    setStatus(result);
  }, [db]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Reflete pendências atualizadas quando o app volta ao foreground (ex.:
  // o AutoSyncListener de app/_layout.tsx acabou de rodar em background)
  // — mesmo padrão de AppState de SessionScreen.tsx.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') refreshStatus();
    });
    return () => subscription.remove();
  }, [refreshStatus]);

  // Estado de rede para o indicador de 3 estados (sincronizado / pendências
  // / offline) — mesma checagem de src/sync/netStatus.ts usada pelo gatilho
  // automático em app/_layout.tsx.
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state) => setOnline(isOnline(state)));
    return unsubscribe;
  }, []);

  const loadProfile = useCallback(async () => {
    if (!ready || !db || !user) return;
    setProfileLoading(true);
    try {
      const profile = await loadOrCreateProfile(db, user.id, () => syncService.pullAll(db));
      setHourlyRateText(String(profile.hourlyRate));
      setMarkupText(String(profile.defaultMarkupPct));
      setLocale(profile.locale);
    } catch (error) {
      console.error('ProfileScreen: falha ao carregar perfil', error);
      Alert.alert(t('profile.edit.errorTitle'), (error as Error).message);
    } finally {
      setProfileLoading(false);
    }
  }, [ready, db, user, t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveProfile = async () => {
    if (!db || !user) return;

    const hourlyRate = parseLocaleNumber(hourlyRateText);
    const defaultMarkupPct = parseLocaleNumber(markupText);
    if (hourlyRate === null || defaultMarkupPct === null) {
      Alert.alert(t('profile.edit.errorTitle'), t('profile.edit.invalidNumberMessage'));
      return;
    }

    setSaving(true);
    try {
      // Sempre via updateRecord (SQLite + outbox) — nunca grava direto no
      // Supabase (regra invíolável #2 do projeto: offline-first).
      await updateRecord(db, profiles, user.id, { hourlyRate, defaultMarkupPct, locale });
      Alert.alert(t('profile.edit.title'), t('profile.edit.saved'));
    } catch (error) {
      console.error('ProfileScreen: falha ao salvar perfil', error);
      Alert.alert(t('profile.edit.errorTitle'), (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const data = await exportUserData();
      const jsonString = JSON.stringify(data, null, 2);
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crochet-export-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        await Share.share({ message: jsonString });
      }
    } catch (error) {
      console.error('ProfileScreen: falha ao exportar dados', error);
      Alert.alert(t('profile.export.errorTitle'), (error as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <View style={styles.container}>
      <Heading style={styles.title}>{t('tabs.profile.title')}</Heading>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('profile.edit.title')}</Text>

        {!ready || profileLoading ? (
          <Text style={styles.muted}>{t('profile.edit.loading')}</Text>
        ) : (
          <>
            <Text style={styles.label}>{t('profile.edit.hourlyRateLabel')}</Text>
            <TextField
              placeholder={t('profile.edit.hourlyRatePlaceholder')}
              value={hourlyRateText}
              onChangeText={setHourlyRateText}
              keyboardType="decimal-pad"
              editable={!saving}
            />

            <Text style={styles.label}>{t('profile.edit.markupLabel')}</Text>
            <TextField
              placeholder={t('profile.edit.markupPlaceholder')}
              value={markupText}
              onChangeText={setMarkupText}
              keyboardType="decimal-pad"
              editable={!saving}
            />

            <Text style={styles.label}>{t('profile.edit.localeLabel')}</Text>
            <View style={styles.localeRow}>
              {AVAILABLE_LOCALES.map((option) => (
                <Chip
                  key={option}
                  label={t('profile.edit.localePtBr')}
                  active={locale === option}
                  onPress={() => setLocale(option)}
                />
              ))}
            </View>

            {saving ? (
              <ActivityIndicator color={colors.sageDeep} />
            ) : (
              <Button title={t('profile.edit.save')} onPress={handleSaveProfile} disabled={!db} />
            )}
          </>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('profile.sync.title')}</Text>

        {!ready ? (
          <Text style={styles.muted}>{t('profile.sync.notReady')}</Text>
        ) : (
          <Text style={[styles.syncIndicator, syncIndicatorStyle(online, pendingTotal, quarantinedTotal)]}>
            {!online
              ? `☁️ ${t('profile.sync.statusOffline')}`
              : quarantinedTotal > 0
                ? `☁️ ${t('profile.sync.statusPendingWithErrors', { count: pendingTotal, quarantined: quarantinedTotal })}`
                : pendingTotal > 0
                  ? `☁️ ${t('profile.sync.statusPending', { count: pendingTotal })}`
                  : `☁️ ${t('profile.sync.statusSynced')}`}
          </Text>
        )}
      </View>

      <Button
        title={t('tabs.profile.preferencesButton')}
        variant="line"
        onPress={() => router.push('/(tabs)/perfil/preferencias')}
      />

      <Button
        title={t('tabs.profile.debugButton')}
        variant="line"
        onPress={() => router.push('/(tabs)/perfil/debug')}
      />

      {exporting ? (
        <ActivityIndicator color={colors.sageDeep} />
      ) : (
        <Button title={t('tabs.profile.exportDataButton')} variant="line" onPress={handleExportData} />
      )}

      <Button
        title={t('tabs.profile.deleteAccountButton')}
        variant="danger"
        onPress={() => router.push('/(tabs)/perfil/excluir-conta')}
      />
    </View>
  );
}

/** Cor discreta por estado — não é o foco visual desta etapa (spec S05
 * §Estados na UI), só distingue "precisa de atenção" (offline/pendências) de
 * "tudo certo" (sincronizado) num relance. Quarentena (Falha #6) usa uma cor
 * própria, mais forte, porque é um estado que não se resolve sozinho (a
 * pendência normal resolve no próximo sync; a quarentena não). */
function syncIndicatorStyle(online: boolean, pendingTotal: number, quarantinedTotal: number) {
  if (!online) return { color: '#b45309' };
  if (quarantinedTotal > 0) return { color: '#dc2626' };
  if (pendingTotal > 0) return { color: '#2563eb' };
  return { color: '#15803d' };
}

const styles = StyleSheet.create({
  container: { flex: 1, alignItems: 'center', padding: spacing.md, backgroundColor: colors.sand },
  title: { marginTop: spacing.md, marginBottom: spacing.sm },
  section: { marginVertical: spacing.md, alignItems: 'center', width: '100%' },
  sectionTitle: { fontFamily: fonts.display, fontSize: 16, color: colors.ink, marginBottom: spacing.sm },
  label: { alignSelf: 'flex-start', fontFamily: fonts.bodyMedium, fontSize: 13, color: colors.inkSoft, marginBottom: spacing.xs },
  localeRow: { width: '100%', flexDirection: 'row', marginBottom: spacing.sm },
  muted: { fontFamily: fonts.body, color: colors.thread },
  syncIndicator: { fontFamily: fonts.bodyBold, fontSize: 15 },
});
